import React, { useState, useEffect, useCallback } from "react";
import { GetUserDetailAudits } from "../apis/get";
import LoaderService from "../../services/LoaderService";
import toast from "react-hot-toast";
import {
  FileText,
  User,
  Activity,
  Clock,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  XCircle,
  X,
  Eye,
  Filter,
  Layers,
  Database
} from "lucide-react";
import { createPortal } from "react-dom";

const formatValue = (val) => {
  if (val === null || val === undefined) return "null";
  if (typeof val === "object") {
    return JSON.stringify(val, null, 2);
  }
  return String(val);
};

const LogDetailModal = ({ log, onClose }) => {
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-black text-white uppercase tracking-wider italic flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500" />
              Audit Log Details
            </h3>
            <span className="text-xs text-slate-500 font-mono">Log ID: #{log.id}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-5 bg-slate-950/30">
          {/* Metadata Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-blue-400" />
                Actor Details
              </span>
              <p className="text-sm text-slate-200 font-bold mt-1">
                ID: <span className="font-mono text-blue-400 select-all">{log.actor_id || "System"}</span>
              </p>
              <p className="text-xs text-slate-400 font-bold mt-0.5">
                Type: <span className="uppercase tracking-wider px-1.5 py-0.5 rounded text-[10px] bg-slate-800 border border-slate-700 text-slate-300">{log.actor_type || "N/A"}</span>
              </p>
            </div>

            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-purple-400" />
                Target User
              </span>
              <p className="text-sm text-slate-200 font-bold mt-1">
                ID: <span className="font-mono text-purple-400 select-all">{log.target_user_id}</span>
              </p>
            </div>

            <div className="bg-slate-950/50 p-4 rounded-xl border border-slate-800/50 space-y-1">
              <span className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-emerald-400" />
                Target Database
              </span>
              <p className="text-sm text-slate-200 font-bold mt-1">
                Table: <span className="font-mono text-emerald-400">{log.table_name}</span>
              </p>
              <p className="text-sm text-slate-200 font-bold">
                Column: <span className="font-mono text-amber-400">{log.column_name}</span>
              </p>
            </div>
          </div>

          {/* Endpoint (If logged) */}
          {(log.endpoint || log.method) && (
            <div className="bg-slate-950/80 p-4 rounded-xl border border-slate-800/80 space-y-1.5">
              <span className="text-xs font-black text-slate-500 uppercase tracking-widest">HTTP Action Context</span>
              <div className="flex items-center gap-2">
                {log.method && (
                  <span className={`text-xs font-black px-2.5 py-0.5 rounded border ${
                    log.method === "GET" ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" :
                    log.method === "POST" ? "text-blue-400 bg-blue-500/10 border-blue-500/20" :
                    log.method === "PUT" ? "text-indigo-400 bg-indigo-500/10 border-indigo-500/20" :
                    log.method === "DELETE" ? "text-red-400 bg-red-500/10 border-red-500/20" :
                    "text-slate-400 bg-slate-500/10 border-slate-500/20"
                  }`}>
                    {log.method}
                  </span>
                )}
                {log.endpoint && (
                  <span className="text-sm font-mono text-slate-300 break-all leading-relaxed">
                    {log.endpoint}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Value Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            {/* Old Value */}
            <div className="flex flex-col space-y-2">
              <span className="text-xs font-black text-rose-400 uppercase tracking-widest">Old Value (Before)</span>
              <pre className="text-sm font-mono text-rose-300 leading-relaxed whitespace-pre-wrap break-all bg-rose-950/20 p-4 rounded-xl border border-rose-900/30 max-h-[35vh] overflow-y-auto custom-scrollbar select-all">
                {formatValue(log.old_value)}
              </pre>
            </div>

            {/* New Value */}
            <div className="flex flex-col space-y-2">
              <span className="text-xs font-black text-emerald-400 uppercase tracking-widest">New Value (After)</span>
              <pre className="text-sm font-mono text-emerald-300 leading-relaxed whitespace-pre-wrap break-all bg-emerald-950/20 p-4 rounded-xl border border-emerald-900/30 max-h-[35vh] overflow-y-auto custom-scrollbar select-all">
                {formatValue(log.new_value)}
              </pre>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-between items-center bg-slate-950/50">
          <span className="text-xs text-slate-500 font-mono">
            {new Date(log.created_at).toLocaleString("en-IN")}
          </span>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-black uppercase tracking-widest rounded-xl transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function UserUpdateValues() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 100,
    total: 0
  });

  const [filters, setFilters] = useState({
    target_user_id: "",
    actor_id: "",
    table_name: "",
    column_name: "",
    date_from: "",
    date_to: ""
  });

  const [tempFilters, setTempFilters] = useState({
    target_user_id: "",
    actor_id: "",
    table_name: "",
    column_name: "",
    date_from: "",
    date_to: ""
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    LoaderService.show();
    try {
      const response = await GetUserDetailAudits(pagination.offset, pagination.limit, filters);
      if (Array.isArray(response)) {
        setLogs(response);
        setPagination(prev => ({
          ...prev,
          total: response.length < prev.limit ? prev.offset + response.length : prev.offset + prev.limit + 1
        }));
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error("Failed to load User detail audits:", error);
      toast.error("Failed to load audit logs");
      setLogs([]);
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  }, [pagination.offset, pagination.limit, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Debounced filters updates
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        target_user_id: tempFilters.target_user_id,
        actor_id: tempFilters.actor_id,
        table_name: tempFilters.table_name,
        column_name: tempFilters.column_name,
        date_from: tempFilters.date_from,
        date_to: tempFilters.date_to
      }));
      setPagination(prev => ({ ...prev, offset: 0 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [
    tempFilters.target_user_id,
    tempFilters.actor_id,
    tempFilters.table_name,
    tempFilters.column_name,
    tempFilters.date_from,
    tempFilters.date_to
  ]);

  const handleTempFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handleResetFilters = () => {
    const cleared = {
      target_user_id: "",
      actor_id: "",
      table_name: "",
      column_name: "",
      date_from: "",
      date_to: ""
    };
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handlePrevPage = () => {
    if (pagination.offset >= pagination.limit) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  const handleNextPage = () => {
    if (logs.length >= pagination.limit) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  // Client-side search filters
  const filteredLogs = logs.filter((log) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (
      log.id?.toString().includes(term) ||
      log.target_user_id?.toLowerCase().includes(term) ||
      log.actor_id?.toLowerCase().includes(term) ||
      log.table_name?.toLowerCase().includes(term) ||
      log.column_name?.toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(pagination.total / pagination.limit) || 1;
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="mt-12 space-y-6 animate-fade-in p-4 sm:p-6 lg:p-8">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative flex items-center gap-4 bg-slate-950 px-6 py-3 rounded-xl border border-slate-800">
            <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <Database className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white tracking-tight uppercase italic flex items-center gap-2">
                User Update Values Logs
              </h1>
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.2em] mt-0.5">
                Profile Changes Audit & Database Value Tracker
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
          {/* Client-side search bar */}
          <div className="relative w-full sm:w-64 group">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-slate-500 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search in page..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-10 py-2.5 bg-slate-900/50 border border-slate-800 text-slate-200 text-base font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all placeholder:text-slate-600 placeholder:font-black placeholder:uppercase placeholder:tracking-widest"
            />
            {searchTerm && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          <button
            onClick={fetchLogs}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-slate-800 transition-all active:scale-95 disabled:opacity-50 group whitespace-nowrap"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'}`} />
            <span className="text-base font-black uppercase tracking-wider">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters (Server-side) */}
      <div className="bg-slate-900/40 border border-slate-800/60 rounded-[2rem] p-6 backdrop-blur-md shadow-2xl space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2 text-white font-black uppercase tracking-wider text-base italic">
            <Filter className="w-5 h-5 text-blue-500" />
            Filter Audit Logs
          </div>
          {(filters.target_user_id || filters.actor_id || filters.table_name || filters.column_name || filters.date_from || filters.date_to) && (
            <span className="text-sm bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider">
              Filters Active
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {/* Target User ID */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Target User ID</label>
            <input
              type="text"
              placeholder="e.g. U12345"
              value={tempFilters.target_user_id}
              onChange={(e) => handleTempFilterChange("target_user_id", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          {/* Actor ID */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Actor ID</label>
            <input
              type="text"
              placeholder="e.g. A98765"
              value={tempFilters.actor_id}
              onChange={(e) => handleTempFilterChange("actor_id", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          {/* Table Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Table Name</label>
            <input
              type="text"
              placeholder="e.g. users"
              value={tempFilters.table_name}
              onChange={(e) => handleTempFilterChange("table_name", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            />
          </div>

          {/* Column Name */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Column Name</label>
            <input
              type="text"
              placeholder="e.g. marital_status"
              value={tempFilters.column_name}
              onChange={(e) => handleTempFilterChange("column_name", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          {/* Start Date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Date From</label>
            <input
              type="datetime-local"
              value={tempFilters.date_from}
              onChange={(e) => handleTempFilterChange("date_from", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col gap-1.5">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Date To</label>
            <input
              type="datetime-local"
              value={tempFilters.date_to}
              onChange={(e) => handleTempFilterChange("date_to", e.target.value)}
              className="block w-full px-4 py-2.5 bg-slate-950 border border-slate-800/80 text-slate-200 text-lg font-bold rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-slate-300"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-800/60">
          <div className="flex items-center gap-2">
            <label className="text-base font-black text-slate-500 uppercase tracking-widest">Page Limit:</label>
            <select
              value={pagination.limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setPagination(prev => ({ ...prev, limit: newLimit, offset: 0 }));
              }}
              className="px-2.5 py-1 bg-slate-950 border border-slate-800 text-slate-300 text-lg font-bold rounded-lg focus:ring-1 focus:ring-blue-500 outline-none"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 bg-slate-950 hover:bg-slate-800 text-slate-400 hover:text-white border border-slate-800/80 rounded-xl transition-all text-sm font-black uppercase tracking-wider"
            >
              Reset
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all text-sm font-black uppercase tracking-wider shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 active:scale-95"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-slate-900/40 border border-slate-800/60 rounded-[2rem] overflow-hidden backdrop-blur-md shadow-2xl relative">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/[0.02] to-transparent pointer-events-none"></div>

        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse relative">
            <thead>
              <tr className="border-b border-slate-800/80 bg-slate-950/40">
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">ID</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Timestamp</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Actor</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Target User</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Table & Column</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest">Value Change</th>
                <th className="px-6 py-5 text-lg font-black text-slate-500 uppercase tracking-widest text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading && filteredLogs.length === 0 ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={7} className="px-6 py-5">
                      <div className="h-4 bg-slate-800 rounded w-full"></div>
                    </td>
                  </tr>
                ))
              ) : filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <tr
                    key={log.id}
                    className="hover:bg-slate-800/30 transition-all duration-200 border-slate-800/30 group"
                  >
                    {/* ID */}
                    <td className="px-6 py-4 text-lg font-mono font-black text-slate-500">#{log.id}</td>

                    {/* Timestamp */}
                    <td className="px-6 py-4 text-lg text-slate-300 font-mono whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                      })}
                    </td>

                    {/* Actor */}
                    <td className="px-6 py-4 text-lg">
                      <div className="flex flex-col gap-1">
                        <span className="font-bold font-mono text-slate-200 select-all truncate max-w-[150px]" title={log.actor_id}>
                          {log.actor_id || "System"}
                        </span>
                        <span className={`text-[10px] font-black uppercase tracking-widest w-fit px-2 py-0.5 rounded border ${
                          log.actor_type === "admin" ? "bg-purple-500/10 text-purple-400 border-purple-500/20" :
                          log.actor_type === "user" ? "bg-sky-500/10 text-sky-400 border-sky-500/20" :
                          "bg-slate-500/10 text-slate-400 border-slate-500/20"
                        }`}>
                          {log.actor_type || "system"}
                        </span>
                      </div>
                    </td>

                    {/* Target User */}
                    <td className="px-6 py-4 text-lg font-mono text-slate-300 font-bold select-all truncate max-w-[150px]" title={log.target_user_id}>
                      {log.target_user_id}
                    </td>

                    {/* Table & Column */}
                    <td className="px-6 py-4 text-lg">
                      <div className="flex flex-col">
                        <span className="font-mono text-slate-300 text-sm">{log.table_name}</span>
                        <span className="font-mono text-blue-400 text-xs font-bold">{log.column_name}</span>
                      </div>
                    </td>

                    {/* Value Change Preview */}
                    <td className="px-6 py-4 text-lg">
                      <div className="flex items-center gap-2 max-w-[250px]">
                        <span className="px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20 text-xs font-mono truncate max-w-[100px]" title={formatValue(log.old_value)}>
                          {formatValue(log.old_value)}
                        </span>
                        <span className="text-slate-500 font-bold">➔</span>
                        <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-mono truncate max-w-[100px]" title={formatValue(log.new_value)}>
                          {formatValue(log.new_value)}
                        </span>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 border border-slate-800 text-slate-300 hover:text-white rounded-lg hover:border-blue-500/30 hover:bg-blue-600/10 text-base font-black uppercase tracking-wider transition-all"
                      >
                        <Eye className="w-4 h-4" />
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-slate-500 text-xs font-black uppercase tracking-widest">
                    No audit logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination controls */}
        <div className="px-6 py-4 border-t border-slate-800/80 bg-slate-950/40 flex items-center justify-between sticky bottom-0 z-10">
          <div className="text-sm font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
            Showing Page {currentPage}
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={pagination.offset === 0 || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={handleNextPage}
              disabled={logs.length < pagination.limit || loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <LogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
}
